import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchBillingInvoices, isBillingForbidden } from "@/api/billing";
import { Button } from "@/components/ui/button";
import { queryKeys } from "@/lib/query-keys";
import { formatMinor } from "./billing-format";

/** Reads invoices independently of sales/credit readiness; missing provider links remain plain text. */
export function InvoiceList({ orgId }: { orgId: string }) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.billing.invoices(orgId),
    queryFn: ({ pageParam }) => fetchBillingInvoices(orgId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => (page.has_more ? page.next_cursor : undefined),
    retry: false,
  });
  if (isBillingForbidden(query.error))
    return (
      <section aria-label="Invoices" className="space-y-5 text-sm">
        <h2 className="text-[17px] font-semibold">Invoices</h2>
        <p role="alert">Invoice access denied</p>
        <p className="text-muted-foreground">
          Your current account is not authorized to view this organization's
          invoices. Contact an organization admin to restore access.
        </p>
      </section>
    );
  const invoices = query.data?.pages.flatMap((page) => page.invoices) ?? [];
  return (
    <section aria-label="Invoices" className="space-y-5 text-sm">
      <div className="flex min-h-[41px] items-start justify-between gap-3 border-b pb-[18px]">
        <h2 className="text-[17px] font-semibold">Invoices</h2>
        {query.isError && (
          <Button
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Retry invoices
          </Button>
        )}
      </div>
      {query.isPending && <p>Loading invoices…</p>}
      {query.isError && (
        <p role="alert">Invoices are temporarily unavailable.</p>
      )}
      {!query.isPending && !query.isError && invoices.length === 0 && (
        <p className="text-muted-foreground">
          {query.data?.pages[0].unavailable_reason === "customer_missing"
            ? "Invoices will appear when billing is set up."
            : "No invoices yet."}
        </p>
      )}
      {invoices.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] text-left">
            <thead>
              <tr className="border-b">
                <th className="p-2">Invoice</th>
                <th className="p-2">Date</th>
                <th className="p-2">Type / status</th>
                <th className="p-2">Total</th>
                <th className="p-2">Paid</th>
                <th className="p-2">Due</th>
                <th className="p-2">Documents</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="border-b">
                  <td className="p-2">{invoice.number ?? invoice.id}</td>
                  <td className="p-2">
                    {new Date(invoice.created_at * 1000)
                      .toISOString()
                      .slice(0, 10)}
                  </td>
                  <td className="p-2">
                    {invoice.type.replaceAll("_", " ")} /{" "}
                    {invoice.status ?? "pending"}
                  </td>
                  <td className="p-2">
                    {formatMinor(invoice.total, invoice.currency)}
                  </td>
                  <td className="p-2">
                    {formatMinor(invoice.amount_paid, invoice.currency)}
                  </td>
                  <td className="p-2">
                    {formatMinor(invoice.amount_due, invoice.currency)}
                  </td>
                  <td className="p-2 space-x-3">
                    {invoice.hosted_invoice_url && (
                      <a
                        className="text-primary underline"
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View invoice
                      </a>
                    )}
                    {invoice.invoice_pdf ? (
                      <a
                        className="text-primary underline"
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Download PDF
                      </a>
                    ) : (
                      <span className="text-muted-foreground">
                        PDF not available
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {query.hasNextPage && (
        <Button
          variant="outline"
          disabled={query.isFetching}
          onClick={() => void query.fetchNextPage()}
        >
          Load more invoices
        </Button>
      )}
    </section>
  );
}
